import { api } from '@/services/apiClient';

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

export interface CookieConsent {
  essential: boolean; // Auth, preferences, security (always true)
  error_tracking: boolean; // Sentry error tracking (optional)
}

export interface CookieConsentState {
  consent: CookieConsent;
  hasConsented: boolean;
  consentDate: Date | null;
}

const CONSENT_STORAGE_KEY = 'subsidypilot_cookie_consent';
const CONSENT_VERSION = '3.0'; // Updated to reflect actual usage (essential + error tracking only)

const DEFAULT_CONSENT: CookieConsent = {
  essential: true, // Always required
  error_tracking: false, // Optional
};

export const useCookieConsent = () => {
  const [state, setState] = useState<CookieConsentState>({
    consent: DEFAULT_CONSENT,
    hasConsented: false,
    consentDate: null,
  });
  const [isLoading, setIsLoading] = useState(true);

  // Load consent from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.version === CONSENT_VERSION) {
          setState({
            consent: { ...DEFAULT_CONSENT, ...parsed.consent },
            hasConsented: true,
            consentDate: parsed.consentDate ? new Date(parsed.consentDate) : null,
          });
        }
      }
    } catch (error) {
      logger.warn('Failed to load cookie consent:', { error });
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save consent to localStorage and database
  const saveConsent = async (consent: Partial<CookieConsent>) => {
    const newConsent = { ...state.consent, ...consent };
    const newState = {
      consent: newConsent,
      hasConsented: true,
      consentDate: new Date(),
    };

    setState(newState);

    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify({
        version: CONSENT_VERSION,
        consent: newConsent,
        consentDate: newState.consentDate?.toISOString(),
      }));

      // Also save to database if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        // Record consent in database for GDPR compliance
        for (const [consentType, consentGiven] of Object.entries(consent)) {
          if (consentType !== 'essential') { // Essential cookies don't need explicit consent
            const data = await api.consentManager({ consent_type: consentType, consent_given: consentGiven, processing_purpose: getConsentPurpose(consentType), });
          }
        }
      }
    } catch (error) {
      logger.warn('Failed to save cookie consent:', { error });
    }
  };

  const getConsentPurpose = (consentType: string): string => {
    const purposes = {
      error_tracking: 'Error monitoring and debugging (Sentry)',
    };
    return purposes[consentType as keyof typeof purposes] || 'General consent';
  };

  // Update specific consent categories
  const updateConsent = (updates: Partial<CookieConsent>) => {
    saveConsent(updates);
  };

  // Accept all non-essential cookies
  const acceptAll = () => {
    saveConsent({
      error_tracking: true,
    });
  };

  // Reject all non-essential cookies
  const rejectAll = () => {
    saveConsent({
      error_tracking: false,
    });
  };

  // Clear all consent (reset to defaults)
  const clearConsent = () => {
    setState({
      consent: DEFAULT_CONSENT,
      hasConsented: false,
      consentDate: null,
    });
    localStorage.removeItem(CONSENT_STORAGE_KEY);
  };

  // Check if specific cookie category is allowed
  const isAllowed = (category: keyof CookieConsent): boolean => {
    return state.consent[category];
  };

  // Check if consent banner should be shown
  const shouldShowBanner = (): boolean => {
    return !state.hasConsented && !isLoading;
  };

  return {
    ...state,
    isLoading,
    updateConsent,
    acceptAll,
    rejectAll,
    clearConsent,
    isAllowed,
    shouldShowBanner,
    saveConsent,
  };
};